import { useAuth, useClerk } from "@clerk/clerk-react";
import { CheckCircle2, Zap, Building2, ArrowRight, FileText, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Section } from "@/layouts/Section";

const REPORT_PACKS = [
  { amount: 5 }, { amount: 30 }, { amount: 60 }, { amount: 120 },
];
const AI_PACKS = [
  { amount: 20 }, { amount: 100 }, { amount: 250 }, { amount: 500 },
];

function PricingCard({ icon: Icon, name, price, period, features, highlight, cta }) {
  return (
    <div
      className={`rounded-2xl border flex flex-col ${
        highlight
          ? "bg-brand-900 border-brand-700 text-white"
          : "bg-card border-border text-foreground"
      }`}
    >
      <div className="px-7 pt-7 pb-6">
        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center mb-4 ${
            highlight ? "bg-brand-700" : "bg-primary/10"
          }`}
        >
          <Icon size={18} className={highlight ? "text-brand-200" : "text-primary"} />
        </div>
        <h2
          className={`font-display text-xl font-normal mb-1 ${
            highlight ? "text-white" : "text-foreground"
          }`}
        >
          {name}
        </h2>
        <div className="flex items-baseline gap-1.5 mb-1">
          <span
            className={`font-display text-[2rem] font-normal ${
              highlight ? "text-white" : "text-foreground"
            }`}
          >
            {price}
          </span>
          {period && (
            <span
              className={`text-sm ${
                highlight ? "text-brand-300" : "text-muted-foreground"
              }`}
            >
              {period}
            </span>
          )}
        </div>
      </div>

      <div
        className={`h-px mx-7 ${highlight ? "bg-brand-700" : "bg-border"}`}
      />

      <div className="px-7 py-6 flex-1">
        <ul className="space-y-3">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-sm">
              <CheckCircle2
                size={15}
                className={`shrink-0 mt-[1px] ${
                  highlight ? "text-brand-400" : "text-primary"
                }`}
              />
              <span className={highlight ? "text-brand-100" : "text-muted-foreground"}>
                {f}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="px-7 pb-7">{cta}</div>
    </div>
  );
}

export default function Pricing() {
  const { isSignedIn } = useAuth();
  const { openSignIn } = useClerk();

  const freeCta = isSignedIn ? (
    <Link to="/dashboard">
      <button className="inline-flex items-center justify-center gap-[0.4rem] px-5 py-[0.72rem] bg-transparent text-foreground text-[0.9375rem] font-medium border border-border rounded-lg cursor-pointer transition-[border-color,transform] duration-150 hover:border-foreground hover:-translate-y-px w-full">
        Go to Dashboard <ArrowRight size={14} />
      </button>
    </Link>
  ) : (
    <button
      onClick={() => openSignIn()}
      className="inline-flex items-center justify-center gap-[0.4rem] px-5 py-[0.72rem] bg-transparent text-foreground text-[0.9375rem] font-medium border border-border rounded-lg cursor-pointer transition-[border-color,transform] duration-150 hover:border-foreground hover:-translate-y-px w-full"
    >
      Get started free <ArrowRight size={14} />
    </button>
  );

  const schoolCta = isSignedIn ? (
    <Link to="/schools/seats">
      <button className="inline-flex items-center justify-center gap-[0.4rem] px-5 py-[0.72rem] bg-brand-500 text-white text-[0.9375rem] font-semibold rounded-lg cursor-pointer transition-[background,transform] duration-150 hover:bg-brand-400 hover:-translate-y-px w-full">
        Set up a school <ArrowRight size={14} />
      </button>
    </Link>
  ) : (
    <button
      onClick={() => openSignIn()}
      className="inline-flex items-center justify-center gap-[0.4rem] px-5 py-[0.72rem] bg-brand-500 text-white text-[0.9375rem] font-semibold rounded-lg cursor-pointer transition-[background,transform] duration-150 hover:bg-brand-400 hover:-translate-y-px w-full"
    >
      Get started <ArrowRight size={14} />
    </button>
  );

  return (
    <div className="pricing-page">

      {/* ── Header ──────────────────────────────────────────────── */}
      <Section variant="light">
        <div className="max-w-[640px] mx-auto">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-primary mb-3">Plans &amp; Pricing</div>
          <h1 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-foreground m-0 mb-4 leading-tight">
            Simple pricing for every classroom.
          </h1>
          <p className="text-[1.0625rem] leading-[1.65] text-muted-foreground m-0 max-w-[520px]">
            Start free — no credit card required. Upgrade when your school is ready.
          </p>
        </div>
      </Section>

      {/* ── Cards ───────────────────────────────────────────────── */}
      <Section variant="muted">
        <div className="max-w-[800px] mx-auto">
          <div className="grid grid-cols-2 gap-6 items-start max-[640px]:grid-cols-1 max-[640px]:max-w-[420px] max-[640px]:mx-auto">
            <PricingCard
              icon={Zap}
              name="Free"
              price="$0"
              period="forever"
              highlight={false}
              features={[
                "20 report credits on signup",
                "20 AI generation credits on signup",
                "All report card types",
                "Class sharing with colleagues",
                "PDF download",
              ]}
              cta={freeCta}
            />
            <PricingCard
              icon={Building2}
              name="School"
              price="$25"
              period="/ teacher / year"
              highlight={true}
              features={[
                "120 report credits per teacher / year",
                "200 AI generation credits per teacher / year",
                "All report card types",
                "Class sharing and school admin dashboard",
                "PDF download",
                "Billing portal + seat management",
              ]}
              cta={schoolCta}
            />
          </div>
        </div>
      </Section>

      {/* ── Credit Packs ────────────────────────────────────────── */}
      <Section variant="light">
        <div className="max-w-[800px] mx-auto">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-primary mb-3">Top up anytime</div>
          <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-foreground m-0 mb-2 leading-tight">
            Need more credits?
          </h2>
          <p className="text-[1.0625rem] leading-[1.65] text-muted-foreground m-0 mb-8 max-w-[520px]">
            Buy report or AI credits in any amount — they never expire and stack with your existing balance.
          </p>
          <div className="grid grid-cols-2 gap-8 max-[560px]:grid-cols-1">
            {/* Report Credits */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <FileText size={14} className="text-primary" /> Report Credits
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {REPORT_PACKS.map(p => (
                  <div key={p.amount} className="border border-border rounded-xl p-4 bg-card text-center">
                    <p className="text-xl font-semibold text-foreground">{p.amount}</p>
                    <p className="text-xs text-muted-foreground">credits</p>
                  </div>
                ))}
              </div>
            </div>
            {/* AI Credits */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Sparkles size={14} className="text-primary" /> AI Credits
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {AI_PACKS.map(p => (
                  <div key={p.amount} className="border border-border rounded-xl p-4 bg-card text-center">
                    <p className="text-xl font-semibold text-foreground">{p.amount}</p>
                    <p className="text-xs text-muted-foreground">credits</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-5">
            Credits can be purchased from the{" "}
            <Link to="/billing" className="underline hover:text-foreground">Billing page</Link>{" "}
            once signed in.
          </p>
        </div>
      </Section>

      {/* ── FAQ strip ───────────────────────────────────────────── */}
      <Section variant="light">
        <div className="max-w-[900px] mx-auto">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-primary mb-3">Common questions</div>
          <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-foreground m-0 mb-8 leading-tight">
            Good to know before you sign up.
          </h2>
          <div className="grid grid-cols-2 gap-x-12 gap-y-8 max-[680px]:grid-cols-1 max-[680px]:gap-y-7">
            {FAQ.map((item) => (
              <div key={item.q} className="border-t border-border pt-5">
                <h3 className="text-[0.9375rem] font-semibold text-foreground m-0 mb-2">{item.q}</h3>
                <p className="text-[0.9rem] leading-[1.65] text-muted-foreground m-0">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── Bottom CTA ──────────────────────────────────────────── */}
      <Section variant="dark">
        <div className="max-w-[1100px] mx-auto text-center">
          <div className="text-xs font-bold tracking-[0.1em] uppercase text-brand-300 mb-3">Get started today</div>
          <h2 className="font-display text-[clamp(1.75rem,3vw,2.5rem)] font-normal text-white m-0 mb-12 leading-tight">
            Try it free — no account needed.
          </h2>
          <div className="flex flex-wrap gap-3 items-center justify-center">
            <Link to="/pgrc">
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-brand-800 text-white text-[0.9375rem] font-semibold rounded-lg cursor-pointer transition-[background,transform] duration-150 hover:bg-brand-700 hover:-translate-y-px no-underline whitespace-nowrap">
                Try a free report card <ArrowRight size={16} />
              </button>
            </Link>
          </div>
        </div>
      </Section>

    </div>
  );
}

const FAQ = [
  {
    q: "What counts as a 'report credit'?",
    a: "Each new student report you create uses one credit. Editing and saving an existing report does not use an additional credit.",
  },
  {
    q: "When does my quota reset?",
    a: "School plan credits reset annually — 120 reports and 200 AI credits per seat each year. Free plan credits are a one-time onboarding gift.",
  },
  {
    q: "Can I share a class with a colleague?",
    a: "Yes. You can share any class with any EasyReports user and give them view or edit access. Shared classes only count against the owner's quota.",
  },
  {
    q: "What happens when my paid plan expires?",
    a: "Your Stripe subscription renews annually. If it lapses, your data stays intact — you just won't be able to create new reports until you renew.",
  },
  {
    q: "How does the free plan work?",
    a: "When you create an account you receive 20 report credits and 20 AI credits as a one-time gift. There's no time limit — use them whenever you like.",
  },
  {
    q: "What report card types are supported?",
    a: "From Progress Reports to Provincial Reports to High School and more. Take a look by opening a free account. We keep adding types based on what teachers need.",
  },
];
